import {
  Box,
  CircularProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { LiveCursorLine } from "components/scrapEyeDataDashboard/VideoPlayer/components/LiveCursorLine";
import { useTimelineZoom } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/TimelineZoomContext";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import { useMemo, useRef } from "react";
import {
  InferenceInputSegment,
  useGetInferenceInputTimeseriesQuery,
} from "src/store/api/generatedApi";

dayjs.extend(utc);

const DEVICE_COLORS = [
  "#1f77b4",
  "#ff7f0e",
  "#2ca02c",
  "#d62728",
  "#9467bd",
  "#8c564b",
  "#e377c2",
  "#7f7f7f",
  "#bcbd22",
  "#17becf",
];

const TIMELINE_HEIGHT = 24;

interface InferenceInputTimelineProps {
  labellingName: string;
  startIso: string;
  endIso: string;
  videoStart: Dayjs;
  duration: number;
}

export const InferenceInputTimeline = ({
  labellingName,
  startIso,
  endIso,
  videoStart,
  duration,
}: InferenceInputTimelineProps) => {
  const { t } = useTenantTranslation();
  const tenantName = useTenant();
  const zoomContext = useTimelineZoom();
  const timelineRef = useRef<HTMLDivElement>(null);

  const { data, isLoading } = useGetInferenceInputTimeseriesQuery({
    tenantName,
    labellingName,
    start: startIso,
    end: endIso,
  });

  const visibleStart = zoomContext?.visibleStart ?? videoStart;
  const visibleDuration = zoomContext?.duration ?? duration;

  const segmentsByAngle = useMemo(() => {
    if (!data?.segments.length)
      return new Map<string, InferenceInputSegment[]>();
    const grouped = new Map<string, InferenceInputSegment[]>();
    for (const segment of data.segments) {
      const existing = grouped.get(segment.angle);
      if (existing) {
        existing.push(segment);
      } else {
        grouped.set(segment.angle, [segment]);
      }
    }
    return grouped;
  }, [data?.segments]);

  const deviceColorMap = useMemo(() => {
    if (!data?.segments.length) return new Map<string, string>();
    const uniqueDevices = [
      ...new Set(data.segments.map((segment) => segment.device_name)),
    ];
    return new Map(
      uniqueDevices.map((name, i) => [
        name,
        DEVICE_COLORS[i % DEVICE_COLORS.length] ?? DEVICE_COLORS[0],
      ])
    );
  }, [data?.segments]);

  if (isLoading) {
    return (
      <Paper
        variant="outlined"
        sx={{ mt: 2, p: 2, bgcolor: "background.default" }}
      >
        <Box display="flex" justifyContent="center" py={1}>
          <CircularProgress size={20} />
        </Box>
      </Paper>
    );
  }

  if (!data || data.segments.length === 0 || visibleDuration <= 0) {
    return null;
  }

  const segmentWidthPercent =
    (data.segment_duration_seconds / visibleDuration) * 100;

  const angles = [...segmentsByAngle.keys()].sort();

  return (
    <Paper
      variant="outlined"
      sx={{ mt: 2, p: 2, bgcolor: "background.default" }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontWeight: 600 }}
        >
          {t("inferenceInput")} ({data.segments.length})
        </Typography>
        <Stack direction="row" spacing={1} sx={{ ml: "auto" }}>
          {[...deviceColorMap.entries()].map(([name, color]) => (
            <Stack key={name} direction="row" spacing={0.5} alignItems="center">
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  bgcolor: color,
                  flexShrink: 0,
                }}
              />
              <Typography variant="caption" color="text.secondary">
                {name}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Stack>
      <Box ref={timelineRef} sx={{ position: "relative", mx: -2 }}>
        <LiveCursorLine
          videoStart={videoStart}
          visibleStart={visibleStart}
          visibleDuration={visibleDuration}
          trackRef={timelineRef}
        />
        <Stack spacing={0.25}>
          {angles.map((angle) => (
            <Box key={angle}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontSize: "0.6rem",
                  lineHeight: 1,
                  pl: "4px",
                }}
              >
                {angle}
              </Typography>
              <Box
                sx={{
                  height: TIMELINE_HEIGHT,
                  bgcolor: "action.hover",
                  borderRadius: 0.5,
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                {segmentsByAngle.get(angle)?.map((segment) => {
                  const segStart = dayjs.utc(segment.segment_start);
                  const segOffsetSeconds = segStart.diff(
                    visibleStart,
                    "second",
                    true
                  );
                  const leftPercent =
                    (segOffsetSeconds / visibleDuration) * 100;

                  if (
                    leftPercent + segmentWidthPercent < 0 ||
                    leftPercent > 100
                  ) {
                    return null;
                  }

                  const color =
                    deviceColorMap.get(segment.device_name) ?? "#1f77b4";
                  return (
                    <Tooltip
                      key={`${segment.segment_start}-${segment.angle}-${segment.device_name}-${segment.physical_id}`}
                      title={
                        <Stack spacing={0.5} sx={{ py: 0.5 }}>
                          <Typography variant="caption">
                            <strong>{t("device")}:</strong>{" "}
                            {segment.device_name}
                          </Typography>
                          <Typography variant="caption">
                            <strong>{t("cameraId")}:</strong>{" "}
                            {segment.physical_id}
                          </Typography>
                          {segment.embedding_model_name ? (
                            <Typography variant="caption">
                              <strong>{t("embeddingModel")}:</strong>{" "}
                              {segment.embedding_model_name}
                            </Typography>
                          ) : null}
                          <Typography variant="caption">
                            <strong>{t("time")}:</strong>{" "}
                            {segStart.format("HH:mm:ss")}
                          </Typography>
                        </Stack>
                      }
                      arrow
                      placement="top"
                    >
                      <Box
                        sx={{
                          position: "absolute",
                          left: `${leftPercent}%`,
                          width: `${segmentWidthPercent}%`,
                          height: "100%",
                          bgcolor: color,
                          borderRight: "1px solid",
                          borderColor: "background.default",
                          "&:hover": { filter: "brightness(1.3)" },
                          cursor: "pointer",
                        }}
                      />
                    </Tooltip>
                  );
                })}
              </Box>
            </Box>
          ))}
        </Stack>
      </Box>
    </Paper>
  );
};
